import {theme} from '../../themes/default-theme';
import React, { useState, useEffect } from 'react';

// Modern hook-based implementation for width detection
const useWidthDetection = () => {
  const [width, setWidth] = useState('lg'); // Default to lg for SSR

  useEffect(() => {
    // Function to get current breakpoint
    const getCurrentWidth = () => {
      if (typeof window === 'undefined') return 'lg';
      
      const keys = [...theme.breakpoints.keys].reverse();
      return keys.find(key => {
        const mediaQuery = theme.breakpoints.only(key);
        return window.matchMedia(mediaQuery.replace('@media ', '')).matches;
      }) || 'xs';
    };

    // Set initial width
    setWidth(getCurrentWidth());

    // Update function for resize events
    const updateDimensions = () => {
      const newWidth = getCurrentWidth();
      setWidth(prevWidth => prevWidth !== newWidth ? newWidth : prevWidth);
    };

    // Add event listener
    window.addEventListener('resize', updateDimensions);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  return width;
};

// HOC wrapper that provides width prop to components
const useWidth = (PassedComponent) => {
  const WidthAwareComponent = React.forwardRef((props, ref) => {
    const width = useWidthDetection();
    const { initialToggleStatus, ...rest } = props;
    
    return (
      <PassedComponent
        {...rest}
        ref={ref}
        width={width}
      />
    );
  });
  
  WidthAwareComponent.displayName = `withWidth(${PassedComponent.displayName || PassedComponent.name})`;
  
  return WidthAwareComponent;
};

// Utility hook for responsive behavior (modern alternative to HOC)
export const useBreakpoint = () => {
  const width = useWidthDetection();
  
  return {
    width,
    isXs: width === 'xs',
    isSm: width === 'sm',
    isMd: width === 'md',
    isLg: width === 'lg',
    isXl: width === 'xl',
    isUp: (breakpoint) => theme.isWidthUp(breakpoint, width),
    isDown: (breakpoint) => theme.isWidthDown(breakpoint, width),
    // Convenience helpers
    isMobile: ['xs', 'sm'].includes(width),
    isTablet: width === 'md',
    isDesktop: ['lg', 'xl'].includes(width),
  };
};

// Component for conditional rendering based on width
const ShowOnWidth = ({ children, show }) => {
  return show ? children : null;
};

// Enhanced ShowOnWidth component with breakpoint support  
export const ResponsiveShow = ({ 
  children, 
  on = [], // Array of breakpoints to show on ['xs', 'sm'] 
  above = null, // Show on this breakpoint and above
  below = null, // Show on this breakpoint and below
  only = null // Show only on this breakpoint
}) => {
  const { width } = useBreakpoint();
  
  let shouldShow = false;
  
  if (only) {
    shouldShow = width === only;
  } else if (above) {
    shouldShow = theme.isWidthUp(above, width);
  } else if (below) {
    shouldShow = theme.isWidthDown(below, width);
  } else if (on.length > 0) {
    shouldShow = on.includes(width);
  }
  
  return shouldShow ? children : null;
};

export { ShowOnWidth, useWidth as default, useWidthDetection }
